import styled from "styled-components";
import { theme } from "../../../theme";
import BodySmall from "../../atoms/typography/BodySmall";
import Tooltip from "../Tooltip";
import { ChatSearchIcon } from "../../atoms/icons/ChatSearchIcon";
import type { CommentModel } from "../../../models/CommentModel";
import { InfoIconSmall } from "../../atoms/icons/InfoIcons";
import dayjs from "dayjs";
import Body from "../../atoms/typography/Body";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { PayByCheckIcon } from "../../atoms/icons/PayByCheckIcon";
import { FileAdminIcon, FileFinanceIcon } from "../../atoms/icons/FileIcons";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Modal from "../Modal";
import { Row } from "react-grid-system";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import Small from "../../atoms/typography/Small";
import { EInvoiceIcon } from "../../atoms/icons/eInvoiceIcon";

interface Props {
  type:
    | "customerComment"
    | "serviceAdmin"
    | "serviceFinance"
    | "gln"
    | "recipientAddress"
    | "comments";
  comment?: string;
  recipientEmails?: Array<string>;
  gln?: string;
  comments?: Array<CommentModel>;
}

const BadgeContainer = styled.div<{ color: string; clickable?: boolean }>`
  background-color: ${(props) => props.color};
  padding: 2px 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 2px;
  width: fit-content;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
`;

const UploadUnitDocumentNotificationsComponents: React.FC<Props> = ({
  type,
  comment,
  recipientEmails,
  gln,
  comments,
}) => {
  const { t } = useTranslation();
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [glnOpen, setGlnOpen] = useState(false);
  const [recipientOpen, setRecipientOpen] = useState(false);

  return (
    <div>
      {type === "customerComment" && (
        <Tooltip
          content={<BodySmall color={theme.colors.white}>{comment}</BodySmall>}
        >
          <BadgeContainer
            color={theme.colors.info20}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setCommentsOpen(!commentsOpen);
            }}
            clickable={true}
          >
            <ChatSearchIcon color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("common.comment").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}
      {type === "recipientAddress" && (
        <Tooltip
          content={
            <BodySmall color={theme.colors.white}>
              {recipientEmails?.join(", ")}
            </BodySmall>
          }
          width="400px"
        >
          <BadgeContainer
            color={theme.colors.hover}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setRecipientOpen(!recipientOpen);
            }}
            clickable={true}
          >
            <PayByCheckIcon color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("common.recipient").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}
      {type === "gln" && (
        <Tooltip
          content={<BodySmall color={theme.colors.white}>{gln}</BodySmall>}
          width="400px"
        >
          <BadgeContainer
            color={theme.colors.textMuted}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setGlnOpen(!glnOpen);
            }}
            clickable={true}
          >
            <EInvoiceIcon color={theme.colors.white} />
            <Small fontweight={400} color={theme.colors.white}>
              {t("common.gln").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}
      {type === "comments" && (
        <Tooltip
          content={
            <>
              {comments?.map((c) => (
                <div key={c.id}>
                  <BodySmall color={theme.colors.white}>
                    {dayjs(c.createdAt).format("YYYY-MM-DD")} -{" "}
                    {c.createdByUser?.firstName} {c.createdByUser?.lastName}
                  </BodySmall>
                  <Body color={theme.colors.white}> {c.content}</Body>
                  <HorizontalSpacer spacing={1} />
                </div>
              ))}
            </>
          }
          width="500px"
        >
          <BadgeContainer color={theme.colors.info05}>
            <InfoIconSmall color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("common.info").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}
      {type === "serviceFinance" && (
        <Tooltip
          content={
            <Body color="white">{t("documentHandler.sellInvoice")}</Body>
          }
        >
          <BadgeContainer color={theme.colors.success05}>
            <FileFinanceIcon color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("documentHandler.sellInvoice").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}
      {type === "serviceAdmin" && (
        <Tooltip
          content={<Body color="white">{t("documentHandler.adminOnly")}</Body>}
        >
          <BadgeContainer color={theme.colors.warning05}>
            <FileAdminIcon color={theme.colors.black} />
            <Small fontweight={400} color={theme.colors.black}>
              {t("documentHandler.admin").toUpperCase()}
            </Small>
          </BadgeContainer>
        </Tooltip>
      )}

      {commentsOpen && (
        <Modal onClose={() => setCommentsOpen(false)} closeIcon width={300}>
          <Row justify="center">
            <Body>{comment}</Body>
          </Row>
          <HorizontalSpacer spacing={2} />
          <Row justify="center">
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => setCommentsOpen(false)}
            />
          </Row>
        </Modal>
      )}
      {glnOpen && (
        <Modal onClose={() => setGlnOpen(false)} closeIcon width={300}>
          <Row justify="center">
            <Body>{gln}</Body>
          </Row>
          <HorizontalSpacer spacing={2} />
          <Row justify="center">
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => setGlnOpen(false)}
            />
          </Row>
        </Modal>
      )}
      {recipientOpen && (
        <Modal onClose={() => setRecipientOpen(false)} closeIcon width={300}>
          <Row justify="center">
            <Body>{recipientEmails?.join(", ")}</Body>
          </Row>
          <HorizontalSpacer spacing={2} />
          <Row justify="center">
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => setRecipientOpen(false)}
            />
          </Row>
        </Modal>
      )}
    </div>
  );
};

export default UploadUnitDocumentNotificationsComponents;
